import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import { m } from 'framer-motion'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import CentralizeAndSimplifyImage from './CentralizeAndSimplifyImage'

const copy = {
  heading: `Centralize and simplify customer data collection across online and offline touchpoints`,
}

const list = [
  {
    title: `Collect`,
    description: `See the complete view of your customer by unifying data across web, mobile web, apps, email, support, and offline interactions.`,
  },
  {
    title: `Understand`,
    description: `Identify traits of your high value customers and uncover key events along the customer journey that impact growth, engagement, and retention.`,
  },
  {
    title: `Centralize`,
    description: `Centralize and scale access to high-quality, secure data and deliver it to the tools and internal teams that need it.`,
  },
]

const link = {
  href: `/product/connections/`,
  text: `Explore Connections`,
}

const CentralizeAndSimplify: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()

  return (
    <SectionBase variant="2Col" copyVariant="narrow" sticky={true}>
      <Section.Copy>
        <CopyTextBase>
          <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
        </CopyTextBase>
        <ColumnTrackWrapper>
          <ColumnTrack gap="medium">
            {list.map(({ title, description }) => (
              <FeatureBoxBase alignment="left" key={title}>
                <FeatureBox.Body>
                  <FeatureBox.Title tag="h3" variant="h5">
                    {title}
                  </FeatureBox.Title>
                  <FeatureBox.Text>{description}</FeatureBox.Text>
                </FeatureBox.Body>
              </FeatureBoxBase>
            ))}
            <ButtonWrapper>
              <ButtonText
                tag="a"
                href={getAbsoluteUrl(link.href, pageConfig)}
                target="_blank"
                iconEnd={ChevronRightIcon}
              >
                {link.text}
              </ButtonText>
            </ButtonWrapper>
          </ColumnTrack>
        </ColumnTrackWrapper>
      </Section.Copy>
      <Section.Contents>
        <Section.Sticky>
          <ImageWrapper
            initial={{ opacity: 0 }}
            whileInView={{
              opacity: 1,
              transition: {
                duration: 0.4,
                delay: 0.1,
              },
            }}
            viewport={{ once: true }}
          >
            <CentralizeAndSimplifyImage />
          </ImageWrapper>
        </Section.Sticky>
      </Section.Contents>
    </SectionBase>
  )
}

export default CentralizeAndSimplify

const ColumnTrackWrapper = styled.div`
  margin-top: 40px;
`
const ButtonWrapper = styled.div``

const ImageWrapper = styled(m.div)``
